import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FaChevronDown, FaChevronRight, FaExpandAlt, FaPlay } from "react-icons/fa";
import { FiTrash2 } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import NoteModal from "../../components/note-modal/note-modal.component";
import { useCombinedAuthHook } from "../../hooks/combined-auth.hook";
import useDatabaseNote from "../../hooks/database-note.hook";
import { useResetScroll } from "../../hooks/reset-scroll.hook";
import { useTitle } from "../../hooks/useTitle.hook";
import { type DatabaseUserNote } from "../../types";
import { encodeString } from "../../utils/crypto-utils";
import { formatSecondsToMMSS } from "../../utils/time";

export type DirtyNote = DatabaseUserNote & { trackId: string };

interface TrackNotesGroup {
  trackId: string;
  trackName: string;
  notes: DirtyNote[];
};

const NotesPage = () => {
  const { user } = useCombinedAuthHook();
  const [notes, setNotes] = useState<DirtyNote[]>([]);
  const [noteToShow, setNoteToShow] = useState<DirtyNote | undefined>(undefined);
  const [updateNotes, setUpdateNotes] = useState(false);
  const [expandedTracks, setExpandedTracks] = useState<Record<string, boolean>>({});
  const [groupedNotes, setGroupedNotes] = useState<TrackNotesGroup[]>([]);
  const { deleteNote, getNotesForUser } = useDatabaseNote();
  const navigate = useNavigate();

  useTitle("Notes");
  useResetScroll();

  const updateNotesForUser = () => {
    getNotesForUser().then((notes) => {
      if (Object.keys(notes).length === 0) return;

      const trackIds = Object.keys(notes);
      const notesWithKeys = trackIds.flatMap((trackId) => {
        return Object.keys(notes[trackId]).map((noteId) => {
          return {
            ...notes[trackId][noteId],
            id: noteId,
            trackId: trackId,
          };
        });
      });
      setNotes(notesWithKeys);
    });
  };

  useEffect(() => {
    if (!user) return;
    updateNotesForUser();
  }, [user]);

  useEffect(() => {
    if (updateNotes) {
      setTimeout(() => {
        updateNotesForUser();
        setUpdateNotes(false);
      }, 100);
    }
  }, [updateNotes]);

  useEffect(() => {
    const trackGroups: Record<string, TrackNotesGroup> = {};

    notes.forEach((note) => {
      if (!trackGroups[note.trackId]) {
        trackGroups[note.trackId] = {
          trackId: note.trackId,
          trackName: ``,
          notes: [],
        };
        setExpandedTracks((prev) => ({
          ...prev,
          [note.trackId]: prev[note.trackId] !== undefined ? prev[note.trackId] : true,
        }));
      }
      trackGroups[note.trackId].notes.push(note);
    });

    const groupedArray = Object.values(trackGroups).sort((a, b) => a.trackName.localeCompare(b.trackName));

    setGroupedNotes(groupedArray);
  }, [notes]);

  const handleRemoveNote = async (authorId: string, fileId: string, id: string) => {
    if (!fileId || !authorId || !id) {
      toast.error("Cannot remove the note now. Please, try again.");
      return;
    };

    try {
      await deleteNote(authorId, fileId, id);
      setNotes((prev) => prev.filter((note) => note.id !== id));
    } catch (e) {
      toast.error("Error removing note. Please try again.");
      return;
    }

    toast.success("Note deleted", {
      duration: 500,
    });
  };

  const takeMeToTrackPage = (note: DirtyNote) => {
    const targetUrl = `/app/track/${encodeString(note.trackAuthorId)}/${note.trackId}?mark=${note.timeStamp}`;
    navigate(targetUrl);
  };

  const toggleTrackExpansion = (trackId: string) => {
    setExpandedTracks((prev) => ({
      ...prev,
      [trackId]: !prev[trackId],
    }));
  };

  return (
    <>
      <div className="flex flex-col items-center min-h-screen bg-gray-50 p-4 sm:p-6">
        <div className={`w-full lg:max-w-4xl bg-white rounded-xl shadow-md overflow-hidden`}>
          <div className="bg-gradient-to-r from-slate-800 to-black py-4 px-6">
            <h1 className="text-2xl font-bold text-white">My Notes</h1>
          </div>

          <div className="p-4 sm:p-6">
            {notes.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="bg-gray-100 rounded-full p-6 mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-12 w-12 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <p className="text-gray-600 text-lg">No notes yet</p>
                <p className="text-gray-500 mt-2 text-center">
                  Your notes will appear here once you create them while listening to tracks.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {groupedNotes.map((group) => (
                  <div key={group.trackId} className="border border-gray-200 rounded-lg overflow-hidden">
                    <div
                      className="bg-gray-50 px-4 py-3 flex justify-between items-center cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => toggleTrackExpansion(group.trackId)}
                    >
                      <div className="flex items-center">
                        {expandedTracks[group.trackId] ? (
                          <FaChevronDown className="text-black mr-2" />
                        ) : (
                          <FaChevronRight className="text-black mr-2" />
                        )}
                        <h3 className="font-medium text-gray-800">{group.trackName}</h3>
                        <span className="ml-2 bg-sky-100 text-sky-800 text-xs font-medium px-2 py-0.5 rounded-full">
                          {group.notes.length} <span className="hidden md:inline">{group.notes.length === 1 ? "note" : "notes"}</span>
                        </span>
                      </div>
                    </div>

                    {expandedTracks[group.trackId] && (
                      <div className="divide-y divide-gray-100">
                        {group.notes
                          .sort((a, b) => a.timeStamp - b.timeStamp)
                          .map((note, idx) => (
                            <div key={note.id} className="p-4 hover:bg-gray-50 transition-colors">
                              <div className="flex justify-between">
                                <div className="flex-grow pr-4">
                                  <div className="flex items-center mb-1">
                                    <span className="bg-sky-100 text-sky-800 text-xs px-2 py-0.5 rounded mr-2">
                                      {formatSecondsToMMSS(note.timeStamp)}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                    </span>
                                  </div>
                                  <p className="text-gray-800 line-clamp-2">{note.noteText}</p>
                                </div>

                                <div className="flex gap-2 items-center">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      takeMeToTrackPage(note)
                                    }}
                                    className="text-black hover:text-sky-800 p-2 rounded-full hover:bg-sky-50 transition-colors has-tooltip"
                                    title="Play the track"
                                  >
                                    <span className="custom-tooltip -ml-9">Play the track</span>
                                    <FaPlay className="w-4 h-4" />
                                  </button>

                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      setNoteToShow(note)
                                    }}
                                    className="text-gray-600 hover:text-gray-800 p-2 rounded-full hover:bg-gray-100 transition-colors has-tooltip"
                                    title="Expand note"
                                  >
                                    <span className="custom-tooltip">Expand</span>
                                    <FaExpandAlt className="w-4 h-4" />
                                  </button>

                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleRemoveNote(note.trackAuthorId, note.trackId, note.id)
                                    }}
                                    className="text-red-500 hover:text-red-700 p-2 rounded-full hover:bg-red-50 transition-colors has-tooltip"
                                    title="Delete note"
                                  >
                                    <span className="custom-tooltip">Delete</span>
                                    <FiTrash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <NoteModal
        activeNote={noteToShow}
        removeActiveNote={() => setNoteToShow(undefined)}
        takeMeToTrackPage={takeMeToTrackPage}
        updateNotes={(b) => setUpdateNotes(b)}
      />
    </>
  )
}

export default NotesPage
